import React, { useMemo } from "react"
import { Redirect } from "react-router"
import { hasAnnotationFieldSchema, hasClassificationFieldSchema } from "resha"
import { AnnotationOutputSchemaItem } from "universal-schema/lib"

import {
    decodeJobListParams,
    defaultJobSortExpression,
    APIJobQueryModel,
    JobStatus
} from "../../../Models/API/JobModel"
import {
    decodeFilterAndSort,
    FilterAndSortExpression,
    encodeFilterAndSort
} from "../../../Models/Client/FilterAndSortExpression"
import { Result } from "../../../Models/Utility/Result"
import { SortExpression } from "../../../Models/Client/SortExpression"
import { findAnnotationOutput } from "../../../Models/Client/AnnotationOutputSchemaItem"

import { JobsListProps } from "../../../Components/Views/Dashboard/Sections/AppDetail/JobsList/JobsList"
import { AppAuthCheck } from "../../../Components/Common/AuthCheck/AppAuthCheck"
import { JobDetail } from "../../../Components/Views/Dashboard/Sections/AppDetail/JobDetail/JobDetail"
import { JobDataViewTabs } from "../../../Components/Views/Dashboard/Sections/AppDetail/JobDetail/DataView"

import { useJobFilterParams } from "../../../Hooks/useJobFilterParams"
import { hasValue } from "../../../Models/Client/FetchState"
import { useAppSchema } from "../../../Hooks/useAppSchema"

import { routePaths } from "../../"
import { AppDetailRouteComponentProps } from "../../RouteProps"
import { AppDetailRoute } from "./AppDetailRoute"

export interface AppDetailRouteParams
    extends FilterAndSortExpression<APIJobQueryModel> {
    /**
     * `true` to tell the intent to open the modal view of the first item of the list.
     * `false` by default.
     */
    openDetail?: boolean
    /**
     * download handle
     */
    d?: string
    /**
     * `true` to clear filter after closing modal. Necessary when modal is opened via 'Review results' button.
     * `false` by default.
     * Intended to be used along with `openDetail=true`.
     */
    clearFilterOnCloseModal?: boolean
    dataTab?: JobDataViewTabs
}

export interface JobsDetailRouteParams
    extends FilterAndSortExpression<APIJobQueryModel> {
    clearFilterOnCloseModal?: boolean
    dataTab?: JobDataViewTabs
}

type ComponentProps = Omit<
    JobsListProps,
    "appID" | "onVisibleItemsChanged" | "isReviewView"
> & {
    d?: string
    dataTab?: JobDataViewTabs
}

export function encodeJobsDetailRouteParams(
    {
        dataTab,
        clearFilterOnCloseModal,
        ...filterAndSort
    }: JobsDetailRouteParams,
    defaultSort: SortExpression<APIJobQueryModel> = defaultJobSortExpression
): URLSearchParams | undefined {
    let search = encodeFilterAndSort(filterAndSort, defaultSort)

    if (clearFilterOnCloseModal) {
        if (search === undefined) {
            search = new URLSearchParams()
        }
        search.set("clearFilterOnCloseModal", "true")
    }

    if (dataTab === "input") {
        if (search === undefined) {
            search = new URLSearchParams()
        }
        search.set("dataTab", dataTab)
    }

    return search
}

export function encodeAppDetailRouteParams(
    {
        openDetail,
        clearFilterOnCloseModal,
        dataTab,
        ...filterAndSort
    }: AppDetailRouteParams,
    defaultSort: SortExpression<APIJobQueryModel> = defaultJobSortExpression
): URLSearchParams | undefined {
    let search = encodeFilterAndSort(filterAndSort, defaultSort)

    if (clearFilterOnCloseModal) {
        if (search === undefined) {
            search = new URLSearchParams()
        }
        search.set("clearFilterOnCloseModal", "true")
    }

    if (openDetail) {
        if (search === undefined) {
            search = new URLSearchParams()
        }
        search.set("open", "true")
    }

    if (dataTab) {
        if (search === undefined) {
            search = new URLSearchParams()
        }
        search.set("dataTab", dataTab)
    }

    return search
}

export function decodeParams(
    orgUsername: string,
    search: URLSearchParams
): Result<ComponentProps> {
    const filterAndSortDecodeResult = decodeFilterAndSort<APIJobQueryModel>(
        search,
        defaultJobSortExpression
    )
    if (filterAndSortDecodeResult.isError) {
        return filterAndSortDecodeResult
    }

    const decodedParams = decodeJobListParams(filterAndSortDecodeResult.value)
    if (decodedParams.isError) {
        return decodedParams
    }

    return Result.ok<ComponentProps>({
        orgUsername,
        filterAndSort: filterAndSortDecodeResult.value,
        params: decodedParams.value,
        jobID: search.get("open") === "true" ? null : undefined,
        dataTab: (search.get("dataTab") as JobDataViewTabs) ?? "output",
        clearFilterOnCloseModal:
            search.get("clearFilterOnCloseModal") === "true",
        d: search.get("d") ?? undefined
    })
}

export function JobDetailRoute(
    props: AppDetailRouteComponentProps<{
        jobID: string
        version?: string
    }>
) {
    const { appID, jobID, version, orgUsername } = props.match.params
    const isReviewView = props.location.pathname.includes(`${jobID}/review`)

    const decoded = useMemo(
        () =>
            decodeParams(
                orgUsername,
                new URLSearchParams(props.location.search)
            ),
        [orgUsername, props.location.search]
    )

    const authCheckProps = {
        orgUsername,
        appID,
        scope: "job:view" as const
    }

    const filterFields = useJobFilterParams(appID, false)

    const schemaFetch = useAppSchema(appID, false)
    const outputSchema = hasValue(schemaFetch)
        ? schemaFetch.value.output
        : undefined

    const annotationItem = useMemo(
        () =>
            outputSchema
                ? findAnnotationOutput(
                      outputSchema,
                      JobStatus.COMPLETED,
                      undefined
                  )
                : undefined,
        [outputSchema]
    )

    // Use previous design if the it's not an annotation App
    if (
        outputSchema &&
        !hasAnnotationFieldSchema(
            (annotationItem as AnnotationOutputSchemaItem) || {}
        ) &&
        !hasClassificationFieldSchema(outputSchema.uiSchema)
    ) {
        return <AppDetailRoute {...props} />
    }

    if (decoded.isError) {
        return <Redirect to={routePaths.appDetail(orgUsername, appID)} />
    }

    return (
        <AppAuthCheck {...authCheckProps}>
            <JobDetail
                key={`${jobID}_${isReviewView}`}
                orgUsername={orgUsername}
                appID={appID}
                jobIDFetch={Result.ok(jobID)}
                versionFetch={
                    version === undefined
                        ? Result.error("")
                        : Result.ok(version)
                }
                filterAndSort={decoded.value.filterAndSort}
                clearFilterOnCloseModal={decoded.value.clearFilterOnCloseModal}
                filterFields={filterFields}
                onClearSelection={() => {}}
                isReviewView={isReviewView}
                dataTab={decoded.value.dataTab}
                // selectedJobs={[]}
            />
        </AppAuthCheck>
    )
}
